import { type ReactElement } from 'react';
import { DashboardDateRangeControls } from '@/features/dashboard/components/DashboardDateRangeControls';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { ExecutionMetricsChartPanel } from '@/features/executions/components/ExecutionMetricsChartPanel';
import { TestExecutionsTable } from '@/features/executions/components/TestExecutionsTable';
import { MetricValueCardsRow } from '@/features/metrics/components/MetricValueCardsRow';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { DetailDeleteHeaderAction } from '@/features/shared/components/DetailDeleteHeaderAction';
import { TestDetailsPanels } from '@/features/tests/components/TestDetailsPanels';
import { s } from '@/theme/tokens';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { Panel } from '@/ui/composites/Panel';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Stack } from '@/ui/layout/Stack';
import { Box } from '@/ui/primitives';
import { Text } from '@/ui/primitives/Text';
import type { TestDetailPageViewModel } from '@/features/tests/hooks/useTestDetailPageViewModel';
import type { TestDetailDto } from '@flowqa/shared';

type TestDetailContentProps = {
  test: TestDetailDto;
  viewModel: TestDetailPageViewModel;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const TestDetailContent = ({
  test,
  viewModel,
  onDelete,
  deleteLoading = false,
}: TestDetailContentProps): ReactElement => {
  const hasCustomDates = Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay);
  const testExecutionsNoDataText = formatRangeWindowNoDataText(
    'No test executions found',
    viewModel.range,
    hasCustomDates,
  );

  return (
    <DetailPageTemplate
      title={
        <Text role="sectionTitle" component="h1">
          <DetailTitlePrefix label="Test" />
          {viewModel.title}
        </Text>
      }
      headerActions={
        <DetailDeleteHeaderAction
          onDelete={onDelete}
          deleteLoading={deleteLoading}
          dialogTitle="Delete Test"
          dialogDescription={
            <Stack gap="sm">
              <Text role="body" color="textSecondary">
                Deleting this test also removes associated test executions.
              </Text>
              <Text role="body" color="textSecondary">
                This action cannot be undone.
              </Text>
            </Stack>
          }
          confirmLabel="Delete Test"
        />
      }
      metadata={<TestDetailsPanels test={test} />}
      primaryContent={
        <Box sx={{ mt: s('md') }}>
          <Stack gap="md">
            <Box
              sx={{
                borderTop: 1,
                borderBottom: 1,
                borderColor: 'divider',
                py: s('lg'),
              }}
            >
              <PageHeader
                title="Test Executions"
                actions={
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap="md"
                    sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
                  >
                    <TagsFilterField
                      value={viewModel.selectedTags}
                      options={viewModel.tagOptions}
                      onChange={viewModel.handleTagsChange}
                      emptyLabel={viewModel.tagOptionsLoading ? 'Loading Tags...' : ''}
                    />
                    <DashboardDateRangeControls
                      value={viewModel.range}
                      onChange={(nextRange) => {
                        viewModel.setRange(nextRange);
                        viewModel.setSelectedStartDay(null);
                        viewModel.setSelectedEndDay(null);
                      }}
                      displayStartDay={viewModel.pickerStartDay}
                      displayEndDay={viewModel.pickerEndDay}
                      isCustomRange={
                        Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay) &&
                        !viewModel.isAllMode
                      }
                      onSelectedStartDayChange={viewModel.handleStartDayChange}
                      onSelectedEndDayChange={viewModel.handleEndDayChange}
                      fullWidthOnMobile
                    />
                  </Stack>
                }
              />
            </Box>
            <Box sx={{ mt: s('md') }}>
              <Stack gap="lg">
                <StatusMetricCardsRow cards={viewModel.testMetricCards} />
                <MetricValueCardsRow items={viewModel.metricValueItems} />
              </Stack>
            </Box>
            <Box sx={{ mt: s('md') }}>
              <ExecutionMetricsChartPanel
                chartTab={viewModel.chartTab}
                tabsAriaLabel="Test detail chart tabs"
                onChartTabChange={viewModel.setChartTab}
                showChartDateClear={viewModel.showChartDateClear}
                onChartDateClear={viewModel.handleChartDateClear}
                chartHeight={viewModel.chartHeight}
                suppressChartAnimation={viewModel.suppressChartAnimation}
                stackedChartData={viewModel.stackedChartData}
                passRateChartData={viewModel.passRateChartData}
                frequencyChartData={viewModel.frequencyChartData}
                durationChartData={viewModel.durationChartData}
                passRateGuideLines={viewModel.passRateGuideLines}
                frequencyGuideLines={viewModel.frequencyGuideLines}
                durationGuideLines={viewModel.durationGuideLines}
                onChartSelect={viewModel.handleChartSelect}
                showLineChartPoints={viewModel.showLineChartPoints}
                emptyStateTitle={testExecutionsNoDataText}
              />
            </Box>
            <Box sx={{ mt: s('lg') }}>
              <Panel title="Executions" padding="md" gap="none" contentGap="md">
                <TestExecutionsTable
                  persistKey={`test-detail-executions-${viewModel.testId}`}
                  fixedFilters={viewModel.testExecutionFilters}
                  pageSizeOptions={[10]}
                  initialPageSize={10}
                  disablePageSizeSelect
                  showSuiteColumn={false}
                  showTestColumn={false}
                  noRowsText={testExecutionsNoDataText}
                />
              </Panel>
            </Box>
          </Stack>
        </Box>
      }
    />
  );
};
